import {Component, effect, inject, input, output} from '@angular/core';
import {TaListBase} from '../../../pages/ta-list-base.directive';
import {BasicTaDetails, SubmissionApproversDetails} from '../../../../api';
import {Button} from 'primeng/button';
import {DatePipe, NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {TableModule} from 'primeng/table';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {ManageApproversModalComponent} from '../../admin/manage-approvers-modal/manage-approvers-modal.component';
import {ApprovalWorkflowStage} from '../../../../util/app.types';
import {toApprovalWorkflow} from '../../../../util/approval-workflow.utils';
import {Badge} from 'primeng/badge';
import {PrepaidIndicatorComponent} from '../../prepaid-indicator/prepaid-indicator.component';
import {AttachmentIndicatorComponent} from '../../attachment-indicator/attachment-indicator.component';
import {TaCommentCount} from '../../ta-comments/ta-comment-count';

@Component({
  selector: 'overdue-approvals-list',
  imports: [
    TaCommentCount,
    Button,
    DatePipe,
    FormsModule,
    InputText,
    NgClass,
    TableModule,
    Badge,
    PrepaidIndicatorComponent,
    AttachmentIndicatorComponent,
  ],
  providers: [
    DialogService,
  ],
  templateUrl: './overdue-approvals-list.component.html',
  styleUrl: './overdue-approvals-list.component.scss',
})
export class OverdueApprovalsListComponent extends TaListBase {
  availableListInput = input<BasicTaDetails[]>([]);
  overrideApproversClicked = output<SubmissionApproversDetails>();

  private dialogService = inject(DialogService);
  ref: DynamicDialogRef | null | undefined;

  constructor() {
    super();
    effect(() => {
      this.availableTaList.set(this.availableListInput());
    });
    // The Edit TA Approvers modal picks the replacement approver from the full TA user list.
    if (this.store.userSearchOptions().length === 0) {
      this.taSearchDatasource.loadUserOptionsForSearch();
    }
  }

  approvalWorkflow(taSubmission: BasicTaDetails): ApprovalWorkflowStage[] {
    return toApprovalWorkflow(taSubmission.approvals);
  }

  /**
   * The approver holding the TA up. The server flags the overdue stage only once its grace period
   * has lapsed, so this falls back to the stage still waiting to be actioned - every TA on this
   * list is stale, and the column should still name someone if the two ever disagree (a request
   * spanning the UTC date rollover, or a row served from cache).
   *
   * A TA held at cancellation approval has the one stage, which toApprovalWorkflow already names
   * for every approver still to action it.
   */
  overdueApproverName(taSubmission: BasicTaDetails): string {
    const stages = this.approvalWorkflow(taSubmission);
    const holdingStage = stages.find(stage => stage.status === 'Overdue')
      ?? stages.find(stage => stage.status === 'Pending');
    return holdingStage?.approverName ?? '-';
  }

  approversModalConfig(taSubmission: BasicTaDetails): any {
    return {
      header: 'Edit TA Approvers',
      inputValues: {
        modalData: taSubmission,
        availableApprovers: this.store.userSearchOptions(),
      },
      contentStyle: {
        overflow: 'auto'
      },
      modal: true,
      width: 'fit',
    };
  }

  openManageApproversModal(taSubmission: BasicTaDetails): void {
    this.ref = this.dialogService.open(ManageApproversModalComponent, this.approversModalConfig(taSubmission));
    this.ref?.onClose.subscribe((details: SubmissionApproversDetails | undefined) => {
      if (!!details) {
        this.overrideApproversClicked.emit(details);
      }
    });
  }
}
